import { useMemo } from 'react';
import { InfoCard } from '@backstage/core-components';
import { useEntity } from '@backstage/plugin-catalog-react';
import { RiRefreshLine, RiDownloadLine, RiFileCopyLine } from '@remixicon/react';
import {
  Button,
  CopyButton,
  CopyableValue,
  ExternalLink,
  Flex,
  InfraField,
  InfraSection,
  InfraStack,
  Text,
  infraCardStyles as styles,
} from '../ui/InfraPrimitives';

export const RIOTXInfoCard = () => {
  const { entity } = useEntity();
  const annotations = entity.metadata.annotations || {};

  const environmentName = annotations['infrastructure.backstage.io/environment-name'];
  const templateType = annotations['infrastructure.backstage.io/type'];
  const sshPrivateKeyEncoded = annotations['migration.infrastructure/ssh-private-key'];
  const sshCommand = annotations['migration.infrastructure/ssh-command'];

  const awsInstanceId = annotations['aws.infrastructure/instance-id'];
  const awsPublicIp = annotations['aws.infrastructure/public-ip'];
  const awsPrivateIp = annotations['aws.infrastructure/private-ip'];
  const awsInstanceType = annotations['aws.infrastructure/instance-type'];
  const awsRegion = annotations['aws.infrastructure/region'];
  const awsSsmCommand = annotations['aws.infrastructure/ssm-command'];

  const gcpInstanceId = annotations['gcp.infrastructure/instance-id'];
  const gcpPublicIp = annotations['gcp.infrastructure/public-ip'];
  const gcpPrivateIp = annotations['gcp.infrastructure/private-ip'];
  const gcpMachineType = annotations['gcp.infrastructure/machine-type'];
  const gcpZone = annotations['gcp.infrastructure/zone'];
  const gcpConsoleUrl = annotations['gcp.infrastructure/console-url'];
  const gcpGcloudSshCommand = annotations['gcp.infrastructure/gcloud-ssh-command'];

  if (!templateType?.includes('riotx')) {
    return null;
  }

  const isAWS = templateType?.startsWith('aws-');

  const sshPrivateKey = useMemo(() => {
    if (!sshPrivateKeyEncoded) return '';
    try {
      return window.atob(sshPrivateKeyEncoded);
    } catch (e) {
      console.error('Failed to decode SSH key:', e);
      return '';
    }
  }, [sshPrivateKeyEncoded]);

  const downloadSSHKey = () => {
    const element = document.createElement('a');
    const file = new Blob([sshPrivateKey], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = 'riotx_key.pem';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const instanceId = awsInstanceId || gcpInstanceId;
  const publicIp = awsPublicIp || gcpPublicIp;
  const privateIp = awsPrivateIp || gcpPrivateIp;
  const instanceType = awsInstanceType || gcpMachineType;
  const region = awsRegion || gcpZone;

  return (
    <InfoCard
      title="🔄 RIOT-X Migration VM"
      subheader={`Environment: ${environmentName || 'N/A'} | Provider: ${isAWS ? 'AWS' : 'GCP'}`}
      icon={<RiRefreshLine size={20} />}
    >
      <InfraStack>
        <InfraSection title={`🖥️ ${isAWS ? 'EC2' : 'Compute'} Instance`}>
          {instanceId && (
            <InfraField
              label="Instance ID:"
              value={
                <span className={styles.copyRow}>
                  {isAWS ? (
                    <ExternalLink
                      href={`https://console.aws.amazon.com/ec2/v2/home?region=${awsRegion}#InstanceDetails:instanceId=${instanceId}`}
                    >
                      {instanceId}
                    </ExternalLink>
                  ) : (
                    <ExternalLink href={gcpConsoleUrl}>{instanceId}</ExternalLink>
                  )}
                  <CopyButton text={instanceId} />
                </span>
              }
              mono={false}
            />
          )}
          {instanceType && (
            <InfraField
              label={isAWS ? 'Instance Type:' : 'Machine Type:'}
              value={instanceType}
            />
          )}
          {region && (
            <InfraField label={isAWS ? 'Region:' : 'Zone:'} value={region} />
          )}
          {publicIp && <CopyableValue label="Public IP:" value={publicIp} />}
          {privateIp && <CopyableValue label="Private IP:" value={privateIp} />}
        </InfraSection>

        {(sshPrivateKey || sshCommand || awsSsmCommand || gcpGcloudSshCommand) && (
          <InfraSection title="🔧 SSH Access">
            {sshPrivateKey && (
              <>
                <InfraField label="SSH Private Key:" value="" mono={false} />
                <Flex gap="2">
                  <Button variant="primary" size="small" onPress={downloadSSHKey}>
                    <RiDownloadLine size={16} />
                    Download Key
                  </Button>
                  <Button
                    variant="secondary"
                    size="small"
                    onPress={() => navigator.clipboard.writeText(sshPrivateKey)}
                  >
                    <RiFileCopyLine size={16} />
                    Copy Key
                  </Button>
                </Flex>
                <Text variant="body-small" style={{ marginTop: '0.5rem', display: 'block' }}>
                  💡 After downloading, run <code>chmod 400 riotx_key.pem</code>
                </Text>
              </>
            )}
            {sshCommand && <CopyableValue label="SSH Command:" value={sshCommand} />}
            {awsSsmCommand && <CopyableValue label="SSM Command:" value={awsSsmCommand} />}
            {gcpGcloudSshCommand && (
              <CopyableValue label="gcloud SSH Command:" value={gcpGcloudSshCommand} />
            )}
          </InfraSection>
        )}
      </InfraStack>
    </InfoCard>
  );
};
